import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Steps, Table, Tag, Space, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CheckOutlined, MailOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import type { BillSendPreviewType, BillSendRecordType } from '../../graphql/types/settlement';
import { GET_BILL_SEND_HISTORY } from '../../graphql/queries/settlement/billSendQueries';
import { useTranslation } from 'react-i18next';

interface BillSendModalProps {
  visible: boolean;
  billId: string;
  preview: BillSendPreviewType | null;
  onCancel: () => void;
  onConfirmSend: () => Promise<void>;
  sendLoading: boolean;
}

const BillSendModal: React.FC<BillSendModalProps> = ({
  visible,
  billId,
  preview,
  onCancel,
  onConfirmSend,
  sendLoading,
}) => {
  const [current, setCurrent] = useState(0); // 0/1

  const latestInitialRecords = preview?.latestSendRecords ?? [];

  const [records, setRecords] = useState<BillSendRecordType[]>(latestInitialRecords);
  const [pageInfo, setPageInfo] = useState<{
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const { t } = useTranslation();

  const [loadHistory] = useLazyQuery(GET_BILL_SEND_HISTORY, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!visible) return;
    setCurrent(0);
    setRecords(latestInitialRecords);
    setPageInfo(null);
  }, [visible, billId, latestInitialRecords]); // billId changes should reset

  // 当进入 Step2 时拉取分页数据（用于“加载更多”）
  useEffect(() => {
    if (!visible || current !== 1) return;
    if (pageInfo) return;

    const pageSize = 10;
    (async () => {
      setLoadingMore(true);
      try {
        const result = await loadHistory({
          variables: {
            billId,
            pageRequest: { page: 1, size: pageSize },
          },
        });
        const payload = result.data?.getBillSendHistory;
        if (payload?.content) setRecords(payload.content);
        if (payload?.pageInfo) setPageInfo(payload.pageInfo);
      } catch {
        // ignore; 允许使用 preview.latestSendRecords 兜底展示
      } finally {
        setLoadingMore(false);
      }
    })();
  }, [visible, current, pageInfo, billId]);

  const emailTags = useMemo(() => preview?.emails ?? [], [preview?.emails]);

  const columns: ColumnsType<BillSendRecordType> = [
    {
      title: t('settlementBillSend.columns.receiverEmail'),
      dataIndex: 'receiverEmail',
      key: 'receiverEmail',
      ellipsis: true,
    },
    {
      title: t('settlementBillSend.columns.senderEmail'),
      dataIndex: 'senderEmail',
      key: 'senderEmail',
      ellipsis: true,
    },
    {
      title: t('settlementBillSend.columns.status'),
      dataIndex: 'sendStatus',
      key: 'sendStatus',
      width: 120,
      render: (status: BillSendRecordType['sendStatus']) => {
        return status === 'SUCCESS' ? (
          <Tag color="green">{t('settlementBillSend.status.success')}</Tag>
        ) : (
          <Tag color="red">{t('settlementBillSend.status.failed')}</Tag>
        );
      },
    },
    {
      title: t('settlementBillSend.columns.sentAt'),
      dataIndex: 'sentAt',
      key: 'sentAt',
      width: 180,
      render: (v: string) => new Date(v).toLocaleString('zh-CN'),
    },
    {
      title: t('settlementBillSend.columns.operator'),
      dataIndex: 'operatorName',
      key: 'operatorName',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('settlementBillSend.columns.errorReason'),
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      ellipsis: true,
      render: (msg?: string | null) => msg || '-',
    },
  ];

  const handleNext = () => {
    setCurrent(1);
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleLoadMore = async () => {
    if (!pageInfo?.hasNext) return;
    setLoadingMore(true);
    try {
      const nextPage = pageInfo.page + 1;
      const pageSize = pageInfo.size;
      const result = await loadHistory({
        variables: {
          billId,
          pageRequest: { page: nextPage, size: pageSize },
        },
      });
      const payload = result.data?.getBillSendHistory;
      if (payload?.content) {
        setRecords(prev => {
          const merged = [...prev, ...payload.content];
          // dedupe by id
          const map = new Map<string, BillSendRecordType>();
          merged.forEach(r => map.set(r.id, r));
          return Array.from(map.values());
        });
      }
      if (payload?.pageInfo) setPageInfo(payload.pageInfo);
    } catch (e) {
      message.error(t('settlementBillSend.messages.loadHistoryFailed'));
    } finally {
      setLoadingMore(false);
    }
  };

  const handleConfirmSend = async () => {
    try {
      await onConfirmSend();
    } catch (e) {
      // parent generally handles message; fallback here
      message.error(t('settlementBillSend.messages.sendFailed'));
    }
  };

  return (
    <Modal
      open={visible}
      title={t('settlementBillSend.title')}
      onCancel={handleCancel}
      width={900}
      footer={null}
      destroyOnClose
    >
      <Steps
        current={current}
        items={[
          { title: t('settlementBillSend.steps.confirmSend') },
          { title: t('settlementBillSend.steps.confirmRecipientsAndSubmit') },
        ]}
      />

      {current === 0 && preview && (
        <div className="mt-4">
          <div className="bg-gray-50 p-4 rounded mb-4">
            <div className="flex gap-6">
              <div>
                <div className="text-gray-500 text-sm">{t('settlementBillSend.labels.customer')}</div>
                <div className="font-medium">{preview.clientName}</div>
              </div>
              <div>
                <div className="text-gray-500 text-sm">{t('settlementBillSend.labels.billPeriod')}</div>
                <div className="font-medium">{preview.billPeriodText}</div>
              </div>
              <div>
                <div className="text-gray-500 text-sm">{t('settlementBillSend.labels.billAmount')}</div>
                <div className="font-medium">{preview.billAmountText}</div>
              </div>
            </div>
          </div>

          <div className="text-sm text-gray-700">
            {t('settlementBillSend.messages.confirmSend')}
          </div>

          <div className="mt-6 flex justify-end">
            <Space>
              <Button onClick={handleCancel}>{t('settlementBillSend.actions.cancel')}</Button>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={handleNext}
              >
                {t('settlementBillSend.actions.confirmContinue')}
              </Button>
            </Space>
          </div>
        </div>
      )}

      {current === 1 && preview && (
        <div className="mt-4">
          <div className="mb-4">
            <div className="text-gray-500 text-sm mb-2">{t('settlementBillSend.labels.recipientEmails')}</div>
            <div>
              {emailTags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {emailTags.map(e => (
                    <Tag key={e} color="blue">
                      {e}
                    </Tag>
                  ))}
                </div>
              ) : (
                <div className="text-red-500">{t('settlementBillSend.messages.noFinanceEmail')}</div>
              )}
            </div>
          </div>

          <div className="mb-3">
            <div className="text-gray-500 text-sm mb-2">{t('settlementBillSend.labels.recentHistory')}</div>
            <Table
              rowKey="id"
              size="small"
              columns={columns}
              dataSource={records}
              pagination={false}
              scroll={{ x: 980 }}
            />
            <div className="mt-3 flex justify-end">
              <Button
                loading={loadingMore}
                disabled={!pageInfo?.hasNext}
                onClick={handleLoadMore}
              >
                {t('settlementBillSend.actions.loadMore')}
              </Button>
            </div>
          </div>

          <div className="text-sm text-gray-700 mb-4">{t('settlementBillSend.messages.finalConfirm')}</div>

          <div className="flex justify-end">
            <Space>
              <Button onClick={handleCancel}>{t('settlementBillSend.actions.cancel')}</Button>
              <Button
                type="primary"
                icon={<MailOutlined />}
                loading={sendLoading}
                onClick={handleConfirmSend}
              >
                {t('settlementBillSend.actions.confirmSend')}
              </Button>
            </Space>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default BillSendModal;

